import React, { useContext } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { timeL, timeM, timeXL } from '../AudiHeader/AudiHeaderStyleUtils';
import type {
  AudiHeaderMenuButtonProps,
  MenuIconAnimatedLinesProps,
} from '../../interfaces/header-components.interfaces';
import {
  iconBottomCloseKeyframes,
  iconBottomOpenKeyframes,
  iconMiddleCloseKeyframes,
  iconMiddleOpenKeyframes,
  iconMiddleSecondCloseKeyframes,
  iconMiddleSecondOpenKeyframes,
  iconTopCloseKeyframes,
  iconTopOpenKeyframes,
} from './AudiHeaderMenuButtonKeyframes';
import type { HeaderStyledMenuButtonWrapperProps } from '../../interfaces/header-styled-components.interfaces';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import { ServiceContext } from '../../context/ServiceContext';
import { ConfigContext } from '../../context/ConfigContext';
import { createPreloadAction } from '../../actions/header-actions';
import { EventAction } from '../../utils/tracking';
import AiSearchIcon from '../AudiHeaderSearchButton/AiSearchIcon';
import { AiSearchButton } from '../AudiHeaderSearchButton/AudiHeaderSearchButton_alpha';
import { IconButton } from '@oneaudi/unified-web-components';

const HeaderStyledMenuButtonWrapper = styled.div<HeaderStyledMenuButtonWrapperProps>`
  display: ${({ isLoading }): string => (isLoading ? 'none' : 'flex')};
  align-items: center;
  flex-shrink: 0;
  order: -1;
  position: relative;
  width: 33%;

  ${({ isSmallView, theme: { breakpoints } }) =>
    !isSmallView &&
    `@media screen and (min-width: ${breakpoints.l}px) {
      display: none;
    }`}

  & button {
    background-color: transparent;
    border: 0 none;
    cursor: pointer;
    display: flex;
    font-size: 1em;
    margin: var(--one-header-space-l) 0;
    order: 1;
    padding: 0;
    text-align: left;
  }
`;

const StyledMenuButtonIcon = styled.div`
  height: 18px;
  margin: 1px var(--one-header-space-s) 3px 3px;
  position: relative;
  width: 18px;
`;

const StyledMenuButtonIconTopLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconTopOpenKeyframes} ${timeXL} forwards;
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconTopCloseKeyframes} ${timeXL};
        `}
  position: absolute;
  right: 0;
  top: 6px;
`;

const StyledMenuButtonIconMiddleLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconMiddleOpenKeyframes} ${timeL} forwards;
          animation-delay: ${timeM};
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconMiddleCloseKeyframes} ${timeL};
        `}
  position: absolute;
  right: 0;
  top: 9px;
`;

const StyledMenuButtonIconMiddleSecondLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconMiddleSecondOpenKeyframes} ${timeL} forwards;
          animation-delay: ${timeM};
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconMiddleSecondCloseKeyframes} ${timeL};
        `}
  position: absolute;
  right: 0;
  top: 9px;
`;

const StyledMenuButtonIconBottomLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconBottomOpenKeyframes} ${timeXL} forwards;
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconBottomCloseKeyframes} ${timeXL};
        `}
  position: absolute;
  right: 0;
  top: 12px;
`;

const StyledSearchButtonWrapper = styled.div`
  margin: var(--one-header-space-l) 0;
  order: 2;

  @media screen and (min-width: ${({ theme: { breakpoints } }) => breakpoints.l}px) {
    display: none;
  }
`;

const AudiHeaderMenuButton: React.FC<AudiHeaderMenuButtonProps> = ({
  label,
  headerNavigationItemsAmount,
  headerDispatch,
  onClick,
  onClickSearch,
  isLoading,
  isOpen,
}) => {
  const { headerStateService, trackingService } = useContext(ServiceContext);
  const { useAiSearch } = useContext(ConfigContext);

  const searchAriaLabel = useI18n({
    defaultMessage: 'Search',
    id: 'nemo.ui.common.search-button',
  });

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  const handleSearchClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    const searchAccessEvent: TrackingDataV2 = {
      event: {
        eventInfo: {
          eventAction: EventAction.NAVIGATION,
          eventName: useAiSearch ? 'one header - ai search access' : 'one header - search access',
        },
        attributes: {
          componentName: EventAction.COMPONENT_NAME,
          label: searchAriaLabel,
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: `${headerNavigationItemsAmount + 1}`,
        },
      },
    };
    trackingService?.track(searchAccessEvent);
    onClickSearch?.();
  };

  const handlePreload = (): void => {
    // @ts-expect-error - preload action type mismatch is expected
    headerDispatch?.(createPreloadAction());
  };

  return (
    <HeaderStyledMenuButtonWrapper
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isLoading={isLoading}
      isOpen={isOpen}
    >
      <button
        aria-label={label}
        onClick={onClick}
        type="button"
        className={`${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME}`}
      >
        <StyledMenuButtonIcon>
          <StyledMenuButtonIconTopLine isOpen={isOpen} />
          <StyledMenuButtonIconMiddleLine isOpen={isOpen} />
          <StyledMenuButtonIconMiddleSecondLine isOpen={isOpen} />
          <StyledMenuButtonIconBottomLine isOpen={isOpen} />
        </StyledMenuButtonIcon>
      </button>
      {onClickSearch &&
        !isOpen &&
        (useAiSearch ? (
          <StyledSearchButtonWrapper>
            <AiSearchButton
              aria-label={searchAriaLabel}
              className={`${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME}`}
              onClick={handleSearchClick}
              onMouseEnter={handlePreload}
              onTouchStart={handlePreload}
              type="button"
            >
              <AiSearchIcon />
            </AiSearchButton>
          </StyledSearchButtonWrapper>
        ) : (
          <StyledSearchButtonWrapper onMouseEnter={handlePreload} onTouchStart={handlePreload}>
            <IconButton
              icon="search"
              aria-label={searchAriaLabel}
              className={`${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME}`}
              onClick={handleSearchClick}
              size="sm"
              variant="tertiary"
              type="button"
            />
          </StyledSearchButtonWrapper>
        ))}
    </HeaderStyledMenuButtonWrapper>
  );
};

export default AudiHeaderMenuButton;
